// ClauseRegistryService - Foundation for clause_id-based navigation system
// Task 50: Enhanced Chunk Context Modal

import { supabase } from './supabase';

// Clause information interface matching ChunkContextModal
export interface ClauseInfo {
  clause_id: string;
  logical_id: string;
  section_heading: string;
  page_start?: number;
  page_end?: number;
  is_definition?: boolean;
  is_schedule?: boolean;
  is_table?: boolean;
  parent_logical_id?: string;
  document_id?: string;
  path_depth?: number;
  char_start?: number;
  char_end?: number;
  text?: string; // Clause text content for highlighting
  clause_type?: string;
  clause_sub_category?: string;
  // Plan 236 Phase C (#974): the literal clause number the source document
  // prints ("4.2", "(a)", "Schedule 1"). Absent on every document uploaded
  // before the column existed — re-extraction is disabled, so there is no
  // second route to populate them in this release. NEVER substitute
  // `logical_id`: it is an internal hierarchy path, and both attempts to
  // derive a displayable number from it failed against production data.
  display_marker?: string | null;
}

// Document context interface
export interface DocumentContext {
  document_name: string;
  document_section?: string;
  clause_hierarchy?: ClauseInfo[];
  chunk_boundaries?: {
    char_start: number;
    char_end: number;
    page_references?: number[];
  };
}

// Chunk-to-clause mapping interface
export interface ChunkClauseMapping {
  chunk_index: number;
  primary_clause_ids: string[];
  partial_clause_ids?: string[];
  clause_boundaries?: Record<string, { start: number; end: number }>;
}

/**
 * ClauseRegistryService - Manages clause metadata for enhanced document navigation
 * 
 * This service provides the foundation for clause_id-based navigation, enabling:
 * - Clause metadata lookup and caching
 * - Chunk-to-clause mapping
 * - Document structure analysis
 * - Navigation breadcrumb generation
 */
export class ClauseRegistryService {
  private clauseCache: Map<string, ClauseInfo> = new Map();
  private documentClausesCache: Map<string, ClauseInfo[]> = new Map();
  private chunkMappingsCache: Map<string, ChunkClauseMapping[]> = new Map();

  /**
   * Get clause information by clause_id
   */
  async getClauseById(clauseId: string): Promise<ClauseInfo | null> {
    // Check cache first
    if (this.clauseCache.has(clauseId)) {
      return this.clauseCache.get(clauseId)!;
    }

    try {
      const { data, error } = await supabase
        .from('document_clauses')
        .select('*')
        .eq('clause_id', clauseId)
        .single();

      if (error) {
        console.warn('Failed to fetch clause by ID:', error);
        return null;
      }

      if (data) {
        const clauseInfo: ClauseInfo = {
          clause_id: data.clause_id,
          logical_id: data.logical_id,
          section_heading: data.section_heading || '',
          page_start: data.page_start,
          page_end: data.page_end,
          is_definition: data.is_definition || false,
          is_schedule: data.is_schedule || false,
          is_table: data.is_table || false,
          parent_logical_id: data.parent_logical_id,
          document_id: data.document_id,
          path_depth: data.path_depth,
          char_start: data.char_start,
          char_end: data.char_end,
        };

        // Cache the result
        this.clauseCache.set(clauseId, clauseInfo);
        return clauseInfo;
      }

      return null;
    } catch (error) {
      console.error('Error fetching clause by ID:', error);
      return null;
    }
  }

  /**
   * Get all clauses for a document with hierarchy structure
   */
  async getClausesForDocument(documentId: string): Promise<ClauseInfo[]> {
    // Check cache first
    if (this.documentClausesCache.has(documentId)) {
      return this.documentClausesCache.get(documentId)!;
    }

    try {
      const { data, error } = await supabase
        .from('document_clauses')
        .select('*')
        .eq('document_id', documentId)
        .order('char_start');  // Sort by document position, not logical_id string

      if (error) {
        console.warn('Failed to fetch clauses for document:', error);
        return [];
      }

      const clauses: ClauseInfo[] = (data || []).map(item => ({
        clause_id: item.clause_id,
        logical_id: item.logical_id,
        section_heading: item.section_heading || '',
        page_start: item.page_start,
        page_end: item.page_end,
        is_definition: item.is_definition || false,
        is_schedule: item.is_schedule || false,
        is_table: item.is_table || false,
        parent_logical_id: item.parent_logical_id,
        document_id: item.document_id,
        path_depth: item.path_depth,
        char_start: item.char_start,
        char_end: item.char_end,
      }));

      // Cache the results
      this.documentClausesCache.set(documentId, clauses);
      
      // Also cache individual clauses
      clauses.forEach(clause => {
        this.clauseCache.set(clause.clause_id, clause);
      });

      return clauses;
    } catch (error) {
      console.error('Error fetching clauses for document:', error);
      return [];
    }
  }

  /**
   * Get chunk-to-clause mappings for a document
   */
  async getChunkClauseMappings(documentId: string): Promise<ChunkClauseMapping[]> {
    // Check cache first
    const cacheKey = `mappings_${documentId}`;
    if (this.chunkMappingsCache.has(cacheKey)) {
      return this.chunkMappingsCache.get(cacheKey)!;
    }

    try {
      const { data, error } = await supabase
        .from('document_chunks')
        .select('chunk_index, primary_clause_ids, partial_clause_ids, clause_boundaries')
        .eq('document_id', documentId)
        .order('chunk_index');

      if (error) {
        console.warn('Failed to fetch chunk mappings:', error);
        return [];
      }

      const mappings: ChunkClauseMapping[] = (data || []).map(item => ({
        chunk_index: item.chunk_index,
        primary_clause_ids: item.primary_clause_ids || [],
        partial_clause_ids: item.partial_clause_ids || [],
        clause_boundaries: item.clause_boundaries || {},
      }));

      // Cache the results
      this.chunkMappingsCache.set(cacheKey, mappings);
      return mappings;
    } catch (error) {
      console.error('Error fetching chunk mappings:', error);
      return [];
    }
  }

  /**
   * Get clause information for a specific chunk
   */
  async getClausesForChunk(documentId: string, chunkIndex: number): Promise<ClauseInfo[]> {
    const mappings = await this.getChunkClauseMappings(documentId);
    const chunkMapping = mappings.find(m => m.chunk_index === chunkIndex);
    
    if (!chunkMapping || !chunkMapping.primary_clause_ids.length) {
      return [];
    }

    // Get clause information for all clause IDs in this chunk
    const clausePromises = chunkMapping.primary_clause_ids.map(clauseId => 
      this.getClauseById(clauseId)
    );

    const clauses = await Promise.all(clausePromises);
    return clauses.filter((clause): clause is ClauseInfo => clause !== null);
  }

  /**
   * Build document context for enhanced chunk display
   */
  async buildDocumentContext(
    documentId: string, 
    chunkIndex?: number,
    chunkText?: string
  ): Promise<DocumentContext | null> {
    try {
      // Get document information
      const { data: documentData, error: docError } = await supabase
        .from('documents')
        .select('filename, document_role')
        .eq('id', documentId)
        .single();

      if (docError || !documentData) {
        console.warn('Failed to fetch document information:', docError);
        return null;
      }

      const context: DocumentContext = {
        document_name: documentData.filename,
        document_section: documentData.document_role || undefined,
      };

      // If chunk information is provided, add clause context
      if (chunkIndex !== undefined) {
        const clauseInfo = await this.getClausesForChunk(documentId, chunkIndex);
        context.clause_hierarchy = clauseInfo;

        // Add chunk boundaries if available
        if (chunkText) {
          context.chunk_boundaries = {
            char_start: 0, // These would come from the chunk data in a real implementation
            char_end: chunkText.length,
            page_references: this.extractPageReferences(clauseInfo),
          };
        }
      }

      return context;
    } catch (error) {
      console.error('Error building document context:', error);
      return null;
    }
  }

  /**
   * Create hierarchy breadcrumb for a clause
   */
  createClauseBreadcrumb(clause: ClauseInfo, allClauses: ClauseInfo[]): string[] {
    const breadcrumbs: string[] = [];
    let currentClause = clause;
    
    // Add current clause
    breadcrumbs.unshift(currentClause.logical_id);
    
    // Find parent clauses in the hierarchy
    while (currentClause.parent_logical_id) {
      const parentClause = allClauses.find(c => c.logical_id === currentClause.parent_logical_id);
      if (!parentClause) break;
      breadcrumbs.unshift(parentClause.logical_id);
      currentClause = parentClause;
    }
    
    return breadcrumbs;
  }

  /**
   * Extract page references from clause information
   */
  private extractPageReferences(clauses: ClauseInfo[]): number[] {
    const pages: Set<number> = new Set();
    
    clauses.forEach(clause => {
      if (clause.page_start) pages.add(clause.page_start);
      if (clause.page_end && clause.page_end !== clause.page_start) {
        for (let p = clause.page_start || 1; p <= clause.page_end; p++) {
          pages.add(p);
        }
      }
    });

    return Array.from(pages).sort((a, b) => a - b);
  }

  /**
   * Clear all caches (useful for testing or memory management)
   */
  clearCache(): void {
    this.clauseCache.clear();
    this.documentClausesCache.clear();
    this.chunkMappingsCache.clear();
  }

  /**
   * Preload clauses for a document (performance optimization)
   */
  async preloadDocument(documentId: string): Promise<void> {
    await Promise.all([
      this.getClausesForDocument(documentId),
      this.getChunkClauseMappings(documentId)
    ]);
  }
}

// Export singleton instance
export const clauseRegistryService = new ClauseRegistryService();

// Note: Interfaces are already exported as types automatically in TypeScript

// Helper function to determine if clause data is available for enhanced display
export const hasClauseSupport = (documentId?: string): boolean => {
  // In a real implementation, this would check if the document has clause data
  // For now, return true if document ID is provided (Phase 2A documents)
  return Boolean(documentId);
};

// Helper function to format clause types for display
export const formatClauseType = (clause: ClauseInfo): string => {
  const types = [];
  if (clause.is_definition) types.push('Definition');
  if (clause.is_schedule) types.push('Schedule'); 
  if (clause.is_table) types.push('Table');
  return types.length > 0 ? types.join(', ') : 'Standard Clause';
};

// Helper function to format page range display
export const formatPageRange = (clause: ClauseInfo): string => {
  if (!clause.page_start) return '';
  if (clause.page_start === clause.page_end) return `Page ${clause.page_start}`;
  return `Pages ${clause.page_start}-${clause.page_end || '?'}`;
};